import React from 'react';

import { Box, Button, Typography } from '@mui/material';

import { shape } from '@app/theme';

import { getRatingBg, getRatingColor, RATINGS } from './RatingScale.helpers';

import type { RatingScaleProperties } from './RatingScale.types';

/**
 * RatingScale — NPS-style 0-10 rating selector with color-coded feedback.
 *
 * Background transitions:
 * - No selection → neutral (action.hover)
 * - 0-3 → red (error.light)
 * - 4-6 → orange (warning.light)
 * - 7-10 → green (success.light)
 */
export const RatingScale: React.FC<RatingScaleProperties> = ({
  value,
  onChange,
  dataTest = 'rating_scale',
}) => {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: value === undefined ? 'action.hover' : getRatingBg(value),
        transition: 'background-color 0.3s',
      }}
      data-testid={dataTest}
    >
      <Typography
        variant='subtitle2'
        data-testid={`${dataTest}_label`}
        sx={{
          fontWeight: 600,
          mb: 1.5
        }}>
        Насколько полезен анализ?
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
        data-testid={`${dataTest}_buttons`}
      >
        {RATINGS.map(ratingValue => {
          const isSelected = value === ratingValue;
          return (
            <Button
              key={ratingValue}
              variant={isSelected ? 'contained' : 'text'}
              size='small'
              onClick={() => onChange(ratingValue)}
              sx={{
                minWidth: 32,
                height: 32,
                p: 0,
                borderRadius: shape.smallRadius / 4,
                fontSize: '0.8125rem',
                fontWeight: isSelected ? 700 : 400,
                ...(isSelected && {
                  backgroundColor: getRatingColor(ratingValue),
                  color: 'common.white',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: getRatingColor(ratingValue),
                    opacity: 0.9,
                    boxShadow: 'none',
                  },
                }),
                ...(!isSelected && {
                  color: 'text.secondary',
                  backgroundColor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    borderColor: 'text.muted',
                    backgroundColor: 'background.paper',
                  },
                }),
              }}
              data-testid={`${dataTest}_${ratingValue}`}
            >
              {ratingValue}
            </Button>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography variant='caption' sx={{
          color: 'text.muted'
        }}>
          Бесполезен
        </Typography>
        <Typography variant='caption' sx={{
          color: 'text.muted'
        }}>
          Отлично
        </Typography>
      </Box>
    </Box>
  );
};
